if (!customElements.get('jj-product-view')) {
  customElements.define(
    'jj-product-view',
    class JjProductView extends HTMLElement {
      constructor() {
        super();

        this.rows = Array.from(this.querySelectorAll('.jj-product-view__row'));
        this.addButton = this.querySelector('.jj-product-view__add');
        this.errorEl = this.querySelector('.jj-product-view__error');
        this.cartDrawer = document.querySelector('cart-drawer');
        this.cartNotification = document.querySelector('cart-notification');

        this.rows.forEach((row) => this.bindRow(row));
        if (this.addButton) {
          this.addButton.addEventListener('click', this.onAdd.bind(this));
        }

        this.bindGallery();
      }

      bindGallery() {
        this.galleryImages = Array.from(this.querySelectorAll('.jj-product-view__image'));
        this.galleryIndex = Math.max(0, this.galleryImages.findIndex((img) => img.classList.contains('is-active')));

        this.warmGalleryCache();
      }

      warmGalleryCache() {
        if (this.galleryImages.length < 2) return;
        const warm = () => {
          this.galleryImages.forEach((img) => {
            if (img.loading === 'lazy') img.loading = 'eager';
          });
        };
        if ('requestIdleCallback' in window) {
          requestIdleCallback(warm, { timeout: 2000 });
        } else {
          setTimeout(warm, 200);
        }
      }

      setMediaIndex(index) {
        if (!Number.isFinite(index)) return;
        if (index < 0 || index >= this.galleryImages.length) return;
        if (index === this.galleryIndex) return;
        const prev = this.galleryImages[this.galleryIndex];
        prev.classList.remove('is-active');
        prev.hidden = true;
        this.galleryIndex = index;
        const next = this.galleryImages[this.galleryIndex];
        next.classList.add('is-active');
        next.hidden = false;
      }

      bindRow(row) {
        const qtyInput = row.querySelector('.jj-product-view__qty');
        const steps = row.querySelectorAll('.jj-product-view__step');
        const nameBtn = row.querySelector('.jj-product-view__row-name[data-media-index]');

        if (nameBtn) {
          nameBtn.addEventListener('click', () => {
            this.setMediaIndex(parseInt(nameBtn.dataset.mediaIndex, 10));
          });
        }

        if (!qtyInput) return;

        steps.forEach((stepBtn) => {
          stepBtn.addEventListener('click', () => {
            const delta = parseInt(stepBtn.dataset.step, 10) || 0;
            const current = parseInt(qtyInput.value, 10) || 0;
            const next = Math.max(0, current + delta);
            qtyInput.value = next;
            qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });

        qtyInput.addEventListener('change', () => {
          const value = parseInt(qtyInput.value, 10);
          if (!Number.isFinite(value) || value < 0) qtyInput.value = 0;
        });
      }

      collectItems() {
        return this.rows
          .map((row) => {
            const qtyInput = row.querySelector('.jj-product-view__qty');
            const id = row.dataset.variantId;
            const quantity = parseInt(qtyInput && qtyInput.value, 10) || 0;
            return { id, quantity };
          })
          .filter((item) => item.id && item.quantity > 0);
      }

      sectionsToRefresh() {
        const sections = ['cart-icon-bubble'];
        if (this.cartDrawer) sections.push('cart-drawer');
        if (this.cartNotification) sections.push('cart-notification-product', 'cart-notification-button');
        return sections;
      }

      onAdd() {
        if (this.addButton.getAttribute('aria-disabled') === 'true') return;

        const items = this.collectItems();
        this.setError('');

        if (items.length === 0) {
          this.setError('Select at least one item.');
          return;
        }

        this.addButton.setAttribute('aria-disabled', 'true');
        this.addButton.classList.add('loading');

        const cartUI = this.cartDrawer || this.cartNotification;
        if (cartUI) cartUI.setActiveElement(document.activeElement);

        const body = {
          items,
          sections: this.sectionsToRefresh().join(','),
          sections_url: window.location.pathname,
        };

        fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.setError(response.description || response.message || 'Could not add to cart.');
              return;
            }

            this.resetQuantities();
            this.refreshCartUI(response);

            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'jj-product-view',
                cartData: response,
              });
            }
          })
          .catch((error) => {
            console.error(error);
            this.setError('Something went wrong. Please try again.');
          })
          .finally(() => {
            this.addButton.removeAttribute('aria-disabled');
            this.addButton.classList.remove('loading');
          });
      }

      refreshCartUI(response) {
        const sections = response.sections || {};

        this.replaceSection(sections['cart-icon-bubble'], 'cart-icon-bubble', '.shopify-section');

        if (this.cartDrawer && sections['cart-drawer']) {
          this.replaceSection(sections['cart-drawer'], 'CartDrawer', '#CartDrawer');
          this.cartDrawer.classList.remove('is-empty');
          this.cartDrawer.open();
          return;
        }

        if (this.cartNotification) {
          this.renderCartNotification(response, sections);
        }
      }

      renderCartNotification(response, sections) {
        const productEl = document.getElementById('cart-notification-product');
        if (productEl && sections['cart-notification-product']) {
          const parsed = new DOMParser().parseFromString(sections['cart-notification-product'], 'text/html');
          const addedKeys = (response.items || []).map((item) => item.key).filter(Boolean);
          const itemsHtml = addedKeys
            .map((key) => parsed.getElementById(`cart-notification-product-${key}`))
            .filter(Boolean)
            .map((el) => el.outerHTML)
            .join('');
          productEl.innerHTML = itemsHtml;
          productEl.classList.toggle('cart-notification-product--multi', addedKeys.length > 1);
        }

        this.replaceSection(sections['cart-notification-button'], 'cart-notification-button', '.shopify-section');

        this.cartNotification.open();
      }

      replaceSection(html, targetId, sourceSelector) {
        if (!html) return;
        const target = document.getElementById(targetId);
        if (!target) return;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(sourceSelector);
        if (!source) return;
        target.innerHTML = source.innerHTML;
      }

      resetQuantities() {
        this.rows.forEach((row) => {
          const qtyInput = row.querySelector('.jj-product-view__qty');
          if (qtyInput) qtyInput.value = 0;
        });
      }

      setError(message) {
        if (!this.errorEl) return;
        if (message) {
          this.errorEl.textContent = message;
          this.errorEl.hidden = false;
        } else {
          this.errorEl.textContent = '';
          this.errorEl.hidden = true;
        }
      }
    }
  );
}
